import { resolveDeviceCatalogKeyFromEquipment } from "./communityDeviceCatalog";

export type CommunityHardwareShareCategory = "keyboard" | "mouse" | "earphone";

const PENDING_ONBOARD_SLOT_KEY = "communitySharePendingOnboardSlot";
const PENDING_NAVIGATE_AFTER_LOGIN_KEY = "communityShareAwaitLoginNavigate";

export interface CommunitySharePendingContext {
  slotKey: number;
  deviceCatalogKey?: string;
}

function readEquipmentDataFromStorage(): Record<string, unknown> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("equipmentData");
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** 当前会话设备 → 配置社区型号 catalogKey */
export function resolveCurrentDeviceCatalogKeyForShare(
  category: "keyboard" | "mouse"
): string | null {
  const equipment = readEquipmentDataFromStorage();
  if (!equipment) return null;
  return resolveDeviceCatalogKeyFromEquipment(equipment, category);
}

/** 从板载配置页带入的槽位与型号（社区分享弹窗预选） */
export function stashCommunityShareOnboardSlot(
  category: CommunityHardwareShareCategory,
  slotKey: number,
  deviceCatalogKey?: string
): void {
  if (typeof window === "undefined") return;
  try {
    const catalogKey =
      deviceCatalogKey ??
      (category === "keyboard" || category === "mouse"
        ? resolveCurrentDeviceCatalogKeyForShare(category)
        : undefined);
    sessionStorage.setItem(
      PENDING_ONBOARD_SLOT_KEY,
      JSON.stringify({
        category,
        slotKey,
        deviceCatalogKey: catalogKey || undefined,
      })
    );
  } catch {
    // ignore
  }
}

export function consumeCommunitySharePendingContext(
  category: CommunityHardwareShareCategory
): CommunitySharePendingContext | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PENDING_ONBOARD_SLOT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_ONBOARD_SLOT_KEY);
    const parsed = JSON.parse(raw) as {
      category?: string;
      slotKey?: number;
      deviceCatalogKey?: string;
    };
    if (parsed.category !== category) return null;
    if (
      typeof parsed.slotKey !== "number" ||
      !Number.isFinite(parsed.slotKey)
    ) {
      return null;
    }
    const deviceCatalogKey =
      typeof parsed.deviceCatalogKey === "string" &&
      parsed.deviceCatalogKey.trim()
        ? parsed.deviceCatalogKey.trim()
        : undefined;
    return { slotKey: parsed.slotKey, deviceCatalogKey };
  } catch {
    return null;
  }
}

/** @deprecated 使用 consumeCommunitySharePendingContext */
export function consumeCommunitySharePendingOnboardSlot(
  category: CommunityHardwareShareCategory
): number | null {
  return consumeCommunitySharePendingContext(category)?.slotKey ?? null;
}

export function navigateToCommunityShareConfig(
  category: CommunityHardwareShareCategory
): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams();
  params.set("deviceCategory", category);
  params.set("shareConfig", "1");
  window.location.href = `/community?${params.toString()}`;
}

function isUserLoggedInForCommunityShare(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(localStorage.getItem("token"));
  } catch {
    return false;
  }
}

/** 当前页唤起登录弹窗（主应用挂载 window.openLoginModal） */
function promptLoginOnCurrentPage(): void {
  if (typeof window === "undefined") return;
  (window as Window & { openLoginModal?: () => void }).openLoginModal?.();
}

function markCommunityShareNavigateAfterLogin(
  category: CommunityHardwareShareCategory
): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(PENDING_NAVIGATE_AFTER_LOGIN_KEY, category);
  } catch {
    // ignore
  }
}

/** 用户取消登录弹窗时清除「登录后进社区」意图（保留已暂存的槽位数据） */
export function abandonCommunityShareNavigateAfterLogin(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(PENDING_NAVIGATE_AFTER_LOGIN_KEY);
  } catch {
    // ignore
  }
}

/**
 * 登录成功后：若来自板载「分享到社区」，跳转配置社区并打开上传弹窗。
 * @returns 是否已触发跳转
 */
export function resumeCommunityShareAfterLoginIfPending(): boolean {
  if (typeof window === "undefined" || !isUserLoggedInForCommunityShare()) {
    return false;
  }
  let category: string | null = null;
  try {
    category = sessionStorage.getItem(PENDING_NAVIGATE_AFTER_LOGIN_KEY);
  } catch {
    return false;
  }
  if (!category) return false;

  try {
    const raw = sessionStorage.getItem(PENDING_ONBOARD_SLOT_KEY);
    if (!raw) {
      abandonCommunityShareNavigateAfterLogin();
      return false;
    }
    const parsed = JSON.parse(raw) as { category?: string };
    if (parsed.category !== category) {
      abandonCommunityShareNavigateAfterLogin();
      return false;
    }
  } catch {
    abandonCommunityShareNavigateAfterLogin();
    return false;
  }

  abandonCommunityShareNavigateAfterLogin();
  navigateToCommunityShareConfig(category as CommunityHardwareShareCategory);
  return true;
}

/** 板载槽位分享到配置社区（跳转并打开对应设备上传弹窗） */
export function openCommunityShareFromOnboardSlot(
  category: CommunityHardwareShareCategory,
  slotKey: number,
  deviceCatalogKey?: string
): void {
  stashCommunityShareOnboardSlot(category, slotKey, deviceCatalogKey);
  if (!isUserLoggedInForCommunityShare()) {
    markCommunityShareNavigateAfterLogin(category);
    promptLoginOnCurrentPage();
    return;
  }
  navigateToCommunityShareConfig(category);
}
